import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../lang";

const EASE = [0.16, 1, 0.3, 1];

export default function Proof({ onBook }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="preuves" ref={ref} className="relative overflow-hidden border-t border-edge/60" data-testid="proof-section">
      <motion.img
        src="https://images.unsplash.com/photo-1481151500463-1fa2dd2d5dbe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwYm9hcmRyb29tJTIwZGFyayUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3ODkzNjg1MTB8MA&ixlib=rb-4.1.0&q=85"
        alt=""
        style={{ y, scale: 1.15 }}
        className="absolute inset-0 h-full w-full object-cover opacity-15 grayscale"
      />
      <div className="relative px-6 py-32 md:px-10 md:py-48">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-signal"
        >
          {t.proof.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="max-w-5xl font-display text-4xl font-semibold leading-[1.05] tracking-tight md:text-7xl"
          data-testid="proof-headline"
        >
          {t.proof.big}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
          className="mt-10 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          {t.proof.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
          className="mt-12"
        >
          <button
            onClick={onBook}
            data-testid="proof-booking-btn"
            className="group flex items-center gap-3 rounded-full bg-foreground px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-void transition-colors duration-200 hover:bg-signal hover:text-white"
          >
            {t.proof.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
